import { Component, type ErrorInfo, type ReactNode } from "react";

import { CrashFallback } from "./CrashFallback";

// A class is the only way React offers to catch a render error. Shared by both
// hosts (CRASH-2): web had one and mobile had none, and the fix for mobile was
// never a second copy.
//
// `onReload` is the host's HARD recovery — a full page reload on web. Mobile
// has no equivalent without a native module, so when it is absent the action
// clears the boundary's own state and re-renders the tree. That is an honest
// retry: it recovers a transient crash and immediately fails again on a
// deterministic one, which is exactly what the athlete needs to be able to
// report.

export type CrashBoundaryProps = {
  children: ReactNode;
  prefersDark: boolean;
  onReload?: () => void;
  /** Prefix for the console line, so a log says which host crashed. */
  logTag: string;
  /** Host hook for the caught error. Mobile records it for the crash
   *  reporter (OP-33); the web passes nothing. */
  onError?: (error: unknown) => void;
};

export class CrashBoundary extends Component<
  CrashBoundaryProps,
  { error: unknown }
> {
  override state: { error: unknown } = { error: undefined };

  static getDerivedStateFromError(error: unknown): { error: unknown } {
    return { error };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    // The console is the only channel left since the crash reporter was
    // removed (2026-09-10), and the fallback already shows the message — this
    // adds the component stack a tester can copy alongside it.
    console.error(`[${this.props.logTag}] render crash`, error, info.componentStack);
    this.props.onError?.(error);
  }

  override render(): ReactNode {
    if (this.state.error === undefined) return this.props.children;
    return (
      <CrashFallback
        error={this.state.error}
        prefersDark={this.props.prefersDark}
        recovery={this.props.onReload ? "reload" : "retry"}
        onAction={
          this.props.onReload ?? (() => this.setState({ error: undefined }))
        }
      />
    );
  }
}
